import { COND, OP } from "../../../misc/RuleObjectTypes";
import React, { useContext, useEffect, useReducer, useState } from "react";
import { RulesContext } from "../../../RulesContext";
import { filterReducer } from "../../../misc/FilterReducer";
import filter from "lodash/filter";
import FilterCondition from "./FilterCondition";
import FilterOperator from "./FilterOperator";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import Button from "Components/DesignSystem/Button/Button";

/**
 * The Add Filters dialog, on the DS Dialog (okf/rules § 4.4, 2026-09-08).
 *
 * What it replaces: a hand-rolled Bootstrap modal — `data-bs-toggle` plumbing on
 * the trigger, a `data-bs-keyboard="false"` shell (Escape deliberately dead), a
 * baked black `.btn-close`, and `.outline-button`/`.button-style` legacy footer
 * buttons. The DS Dialog brings the § 17 chrome, focus management, and Escape
 * (routed to the same `onClose` as every other dismissal).
 *
 * **This component stays MOUNTED while the dialog is closed — deliberately.**
 * The draft filters live in the `useReducer` below, and under Bootstrap the
 * modal was always in the DOM (just hidden), so un-applied edits SURVIVED a
 * Close and greeted you on reopen. Keeping the reducer OUTSIDE the Dialog (which
 * unmounts its children on close) preserves that behaviour exactly — the "no
 * logic change" constraint of the migration. Open/close state belongs to
 * `SubCondition`, which owns one dialog per side.
 *
 * The filter ROWS migrated with it (DS Dropdowns, DS IconButtons) — except the
 * attribute-VALUE picker, which stays react-select: its API-paged lazy loading
 * (onMenuScrollToBottom + debounced search) has no DS equivalent yet.
 */
const Filters = ({ attributesList, filters, side, rulePos, savedFilters, isOpen = false, onClose }) => {
  const { ruleDispatch } = useContext(RulesContext);
  let [filterSet, reduceFilters] = useReducer(filterReducer, {
    localFilters: filters,
  });

  const [disableApplyFilter, setDisableApplyFilter] = useState(false);

  const categoryAttributeSet = attributesList && attributesList;

  useEffect(() => {
    let filteredArray = filter(
      filterSet.localFilters,
      (item) =>
        item.AttrValueId === 0 ||
        item.conditionOperator === 0 ||
        item.attrID === 0
    );
    if (filteredArray.length > 0) {
      setDisableApplyFilter(true);
    } else {
      setDisableApplyFilter(false);
    }
  });

  const clearFilters = () => {
    reduceFilters({
      type: "SET_FILTERS",
      filters: [
        {
          type: "Condition",
          attrID: 0,
          AttrValueId: 0,
          conditionOperator: 0,
        },
      ],
    });
    ruleDispatch({
      type: "APPLY_FILTERS",
      side: side,
      filtersArray: [
        {
          type: "Condition",
          attrID: 0,
          AttrValueId: 0,
          conditionOperator: 0,
        },
      ],
      position: rulePos,
    });
  }

  const applyFilters = () => {
    ruleDispatch({
      type: "APPLY_FILTERS",
      side: side,
      filtersArray: filterSet.localFilters,
      position: rulePos,
    });
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => onClose?.()}
      title="Filters"
      size="lg"
      data-testid={`rule-filters-dialog-${side}`}
      footer={
        <div className="tw-flex tw-w-full tw-justify-end tw-gap-2">
          {/* The same three actions the Bootstrap footer had, on the § 114 footer
              grammar: the quiet dismiss, the outlined secondary, the one primary.
              Clear and Apply also dismissed before (data-bs-dismiss) — kept. */}
          <Button
            variant="tertiary"
            onClick={() => onClose?.()}
            data-testid="rule-filter-close-btn"
          >
            Close
          </Button>
          <Button
            variant="secondary"
            data-testid="rule-filter-clear-btn"
            onClick={() => { clearFilters(); onClose?.(); }}
          >
            Clear Filter(s)
          </Button>
          <Button
            variant="primary"
            data-testid="filters-filters-button-2"
            disabled={disableApplyFilter}
            onClick={() => { applyFilters(); onClose?.(); }}
          >
            Apply Filters
          </Button>
        </div>
      }
    >
      <div className="filter-section">
        {filterSet.localFilters &&
        filterSet.localFilters.length > 0 ? (
          filterSet.localFilters.map((element, position) => {
            if (element.type === COND) {
              return (
                <FilterCondition
                  {...{
                    element,
                    position,
                    reduceFilters,
                    categoryAttributeSet,
                    filterCount: filterSet.localFilters.length,
                  }}
                  key={position}
                />
              );
            } else if (element.type === OP)
              return (
                <FilterOperator
                  {...{
                    element,
                    position,
                    reduceFilters,
                  }}
                  key={position}
                />
              );
          })
        ) : (
          <FilterCondition
            {...{
              reduceFilters,
              categoryAttributeSet,
              filterCount: 0,
            }}
          />
        )}
      </div>
    </Dialog>
  );
};

export default Filters;
